import React, { useEffect, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import ReactMarkdown from 'react-markdown';
import { FiX, FiDownload } from 'react-icons/fi';

interface LegalDocModalProps {
  docKey: 'terms' | 'privacy';
  onClose: () => void;
}

const LEGAL_DOCS: Record<string, { title: string; file: string }> = {
  terms: { title: 'Terms of Service', file: 'terms-v1' },
  privacy: { title: 'Privacy Policy', file: 'privacy-v1' },
};

export const LegalDocModal: React.FC<LegalDocModalProps> = ({ docKey, onClose }) => {
  const { darkMode } = useTheme();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  const docInfo = LEGAL_DOCS[docKey];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const module = await import(`../../legal/${docInfo.file}.md?raw`);
        const stripped = module.default.replace(/<!--[\s\S]*?-->/g, '').trimStart();
        setContent(stripped);
      } catch {
        setContent('# Document Not Found\n\nThe requested legal document could not be loaded.');
      }
      setLoading(false);
    };
    load();
  }, [docKey, docInfo.file]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${docInfo.file}.md`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-70"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={`relative flex flex-col w-full max-w-3xl mx-4 rounded-xl shadow-2xl
          ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-800'}
          max-h-[90vh]`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-6 py-4 border-b flex-shrink-0
            ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
        >
          <h2 className="text-lg font-semibold">{docInfo.title}</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownload}
              disabled={loading}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border transition-colors
                ${darkMode
                  ? 'border-gray-600 text-gray-400 hover:text-white hover:border-gray-400'
                  : 'border-gray-300 text-gray-500 hover:text-gray-800 hover:border-gray-500'
                }`}
            >
              <FiDownload size={13} />
              Download .md
            </button>
            <button
              onClick={onClose}
              className={`p-1.5 rounded-md transition-colors
                ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              aria-label="Close"
            >
              <FiX size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-6">
          {loading ? (
            <div className="flex justify-center items-center h-48">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
            </div>
          ) : (
            <div className={`prose max-w-none ${darkMode ? 'prose-invert' : ''}`}>
              <ReactMarkdown>{content}</ReactMarkdown>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`flex justify-end px-6 py-4 border-t flex-shrink-0
            ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
        >
          <button
            onClick={onClose}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-colors
              ${darkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-white'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
              }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
